import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import theme from '../theme';
import { TerminalBox, TerminalCard, TerminalButton, TerminalTitle, TerminalSubtitle, TerminalText } from './shared/TerminalBox';

const Container = styled.div`
  min-height: 100vh;
  background: ${theme.colors.bg};
  color: ${theme.colors.text};
  padding: 2rem;
  position: relative;
`;

const HeroSection = styled.section`
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
  position: relative;
`;

const HeroContent = styled.div`
  max-width: 900px;
  z-index: 1;
`;

const HeroTitle = styled(TerminalTitle)`
  font-size: 4rem;
  margin-bottom: 1rem;
  animation: glow 2s ease-in-out infinite alternate;
  
  @keyframes glow {
    from {
      text-shadow: 0 0 20px ${theme.colors.primary}, 0 0 30px ${theme.colors.primary};
    }
    to {
      text-shadow: 0 0 30px ${theme.colors.primary}, 0 0 40px ${theme.colors.primary};
    }
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(TerminalSubtitle)`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  color: ${theme.colors.textSecondary};
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const TerminalPrompt = styled.div`
  font-family: ${theme.fonts.mono};
  color: ${theme.colors.primary};
  font-size: 1.1rem;
  margin-bottom: 2rem;
  text-align: left;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  &::before {
    content: '${theme.terminal.prompt} ';
    color: ${theme.colors.primary};
  }
`;

const HeroDescription = styled.p`
  font-family: ${theme.fonts.mono};
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${theme.colors.text};
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const Section = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled(TerminalSubtitle)`
  font-size: 2rem;
  text-align: center;
  margin-bottom: 3rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  
  &::before {
    content: '> ';
    color: ${theme.colors.primary};
  }
  
  &::after {
    content: ' <';
    color: ${theme.colors.primary};
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const FeatureCard = styled(TerminalCard)`
  text-align: left;
  transition: all ${theme.transitions.normal};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.glowStrong};
  }
`;

const FeatureTitle = styled.h3`
  font-family: ${theme.fonts.mono};
  font-size: 1.3rem;
  font-weight: 700;
  color: ${theme.colors.text};
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &::before {
    content: '> ';
    color: ${theme.colors.primary};
  }
`;

const FeatureDescription = styled.p`
  font-family: ${theme.fonts.mono};
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${theme.colors.textSecondary};
`;

const ModulesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const ModuleCard = styled(TerminalBox)`
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  text-align: left;
  
  &:hover {
    transform: translateY(-3px);
    border-color: ${theme.colors.primaryLight};
  }
`;

const ModuleTitle = styled.h4`
  font-family: ${theme.fonts.mono};
  font-size: 1.1rem;
  font-weight: 700;
  color: ${theme.colors.text};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  
  &::before {
    content: '[ ';
    color: ${theme.colors.primary};
  }
  
  &::after {
    content: ' ]';
    color: ${theme.colors.primary};
  }
`;

const ModuleDescription = styled.p`
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  color: ${theme.colors.textSecondary};
  line-height: 1.5;
`;

const Footer = styled.footer`
  text-align: center;
  padding: 3rem 2rem;
  border-top: 1px solid ${theme.colors.border};
  margin-top: 4rem;
  font-family: ${theme.fonts.mono};
  color: ${theme.colors.textMuted};
  font-size: 0.875rem;
`;

const BlinkingCursor = styled.span`
  display: inline-block;
  width: 10px;
  height: 20px;
  background: ${theme.colors.primary};
  animation: blink ${theme.terminal.blinkSpeed} infinite;
  margin-left: 4px;
  
  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
`;

const Home = () => {
  const navigate = useNavigate();
  const [typedText, setTypedText] = useState('');
  const fullText = 'initializing_blockchain_interface...';

  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      if (index < fullText.length) {
        setTypedText(fullText.slice(0, index + 1));
        index++;
      } else {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const handleGetStarted = () => {
    navigate('/pricing');
  };

  const handleTryAPI = (path) => {
    navigate(path);
  };

  return (
    <Container>
      <HeroSection>
        <HeroContent>
          <TerminalPrompt>
            {typedText}
            <BlinkingCursor />
          </TerminalPrompt>
          <HeroTitle>QUANTDELTA</HeroTitle>
          <HeroSubtitle>Secure. Intelligent. Modular.</HeroSubtitle>
          <HeroDescription>
            Your gateway to cryptographic APIs, secure VPN infrastructure, and enterprise-grade tooling—designed for regulated industries.
          </HeroDescription>
          <ButtonGroup>
            <TerminalButton onClick={handleGetStarted}>
              Start Building
            </TerminalButton>
            <TerminalButton onClick={() => handleTryAPI('/signin')}>
              Access APIs
            </TerminalButton>
          </ButtonGroup>
        </HeroContent>
      </HeroSection>

      <Section>
        <SectionTitle>Core Capabilities</SectionTitle>
        <FeaturesGrid>
          <FeatureCard>
            <FeatureTitle>Encrypts Everything</FeatureTitle>
            <FeatureDescription>
              From AES to RSA, SHA256 to HMAC—our cryptographic APIs give you simple, production-ready endpoints for encryption, signing, and token generation.
            </FeatureDescription>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureTitle>Secures The Flow</FeatureTitle>
            <FeatureDescription>
              Every QUANTDELTA account includes built-in VPN access and identity-shielded API routing. Every request is encrypted. Every transaction is protected.
            </FeatureDescription>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureTitle>Protects Infrastructure</FeatureTitle>
            <FeatureDescription>
              Zero-trust architecture, clean audit trails, and modular endpoints let you build with confidence—without exposing your backend or managing your own security stack.
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>
      </Section>

      <Section>
        <SectionTitle>API Modules</SectionTitle>
        <ModulesGrid>
          <ModuleCard onClick={() => handleTryAPI('/cryptography')}>
            <ModuleTitle>Encryption API</ModuleTitle>
            <ModuleDescription>
              AES, RSA, digital signatures, HMACs, secure token generation
            </ModuleDescription>
          </ModuleCard>
          
          <ModuleCard>
            <ModuleTitle>Zero-Log VPN</ModuleTitle>
            <ModuleDescription>
              End-to-end encrypted browsing, IPv6/DNS/WebRTC leak protection, global routing
            </ModuleDescription>
          </ModuleCard>
          
          <ModuleCard onClick={() => handleTryAPI('/economic-events')}>
            <ModuleTitle>Economic Calendar</ModuleTitle>
            <ModuleDescription>
              Global macro events, earnings releases, and key indicators
            </ModuleDescription>
          </ModuleCard>
          
          <ModuleCard onClick={() => handleTryAPI('/cryptography')}>
            <ModuleTitle>Token Generator</ModuleTitle>
            <ModuleDescription>
              UUIDs, secure salts, and session keys for auth and crypto use
            </ModuleDescription>
          </ModuleCard>
          
          <ModuleCard onClick={() => handleTryAPI('/screeners')}>
            <ModuleTitle>Market Screeners</ModuleTitle>
            <ModuleDescription>
              Real-time stock and crypto market screening with advanced filters
            </ModuleDescription>
          </ModuleCard>
          
          <ModuleCard onClick={() => handleTryAPI('/ohlcv')}>
            <ModuleTitle>OHLCV Charts</ModuleTitle>
            <ModuleDescription>
              Real-time price data and candlestick charts for any symbol
            </ModuleDescription>
          </ModuleCard>
        </ModulesGrid>
      </Section>

      <Section>
        <SectionTitle>Built For Financial-Grade Infrastructure</SectionTitle>
        <TerminalCard style={{ textAlign: 'center', padding: '3rem' }}>
          <TerminalText style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
            Whether you're building crypto wallets, banking tools, data aggregators, privacy-first SaaS, or compliance-grade enterprise systems—QUANTDELTA gives you the tools to build securely, scale efficiently, and protect every layer of your stack.
          </TerminalText>
        </TerminalCard>
      </Section>

      <Section>
        <ButtonGroup>
          <TerminalButton onClick={handleGetStarted} style={{ fontSize: '1.1rem', padding: '1rem 2rem' }}>
            Get Started
          </TerminalButton>
          <TerminalButton onClick={() => handleTryAPI('/cryptography')} style={{ fontSize: '1.1rem', padding: '1rem 2rem' }}>
            Explore APIs
          </TerminalButton>
        </ButtonGroup>
      </Section>

      <Footer>
        <TerminalText className="prompt">
          © 2025 QUANTDELTA. All rights reserved.
        </TerminalText>
      </Footer>
    </Container>
  );
};

export default Home;
